import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as apiLearning from "../../../../api/learning";
import { useSelector, shallowEqual } from "react-redux";
import { isAgente } from "../../../../config/config";

export default function QuizResults({ token, isAllowed, videos, onSelect, onChangeSelect }) {
  const { user } = useSelector(({ auth }) => auth, shallowEqual);
  const userIsAgente = isAgente(parseInt(user.idRole));

  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ userId: null, courseId: null });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.2)" : "none",
      "&:hover": { borderColor: "#3b82f6" },
      borderRadius: "0.5rem",
      padding: "2px",
    }),
    menu: (base) => ({ ...base, zIndex: 99999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#3b82f6" : "transparent",
      color: state.isSelected ? "white" : "inherit",
    }),
  };

  const videoOptions = useMemo(() => (Array.isArray(videos) ? videos : []).map((v) => ({
    value: v.video_id,
    label: `${v.video_id} • ${v.title}`,
  })), [videos]);

  const userOptions = useMemo(() => (Array.isArray(users) ? users : []).map((u) => ({
    value: u.id,
    label: `${u.fullname} • ${u.email}`,
  })), [users]);

  useEffect(() => {
    if (!token || !isAllowed) return;
    apiLearning
      .getUsers(token)
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
  }, [token, isAllowed]);

  const loadResults = async (nextFilters = filters) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await apiLearning.getQuizResults(token, {
        videoId: nextFilters.courseId || undefined,
        userId: (userIsAgente ? user.id : nextFilters.userId || undefined),
      });
      setResults(Array.isArray(res) ? res : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResults(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <section className="tw:bg-white tw:rounded-xl tw:shadow-xs tw:overflow-hidden tw:border tw:border-gray-100">
      <div className="tw:bg-gray-50 tw:px-6 tw:py-4">
        <h2 className="tw:text-xl tw:text-gray-800">Risultati Quiz</h2>
      </div>
      <div className="tw:p-6 tw:space-y-4">
        <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
          <div>
            <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">Filtra per corso</label>
            <Select
              classNamePrefix="tw"
              options={videoOptions}
              value={filters.courseId ? videoOptions.find((o) => o.value === filters.courseId) || null : null}
              onChange={(opt) => {
                const next = { ...filters, courseId: opt ? opt.value : null };
                setFilters(next);
                loadResults(next);
                onChangeSelect && onChangeSelect(null)

              }}
              placeholder="— Seleziona corso —"
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
          <div>
            <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">Filtra per utente</label>
            <Select
              classNamePrefix="tw"
              options={userOptions}
              value={filters.userId ? userOptions.find((o) => o.value === filters.userId) || null : null}
              onChange={(opt) => {
                const next = { ...filters, userId: opt ? opt.value : null };
                setFilters(next);
                loadResults(next);
                onChangeSelect && onChangeSelect(null)
              }}
              placeholder="— Seleziona utente —"
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        </div>

        <div className="tw:mt-4">
          {loading ? (
            <div className="tw:flex tw:items-center tw:gap-2 tw:text-gray-600">
              <FontAwesomeIcon icon={faSpinner} className="tw:animate-spin" />
              Caricamento risultati...
            </div>
          ) : results.length === 0 ? (
            <div className="tw:text-sm tw:text-gray-500">Nessun risultato trovato.</div>
          ) : (
            <div className="tw:overflow-x-auto">
              <table className="tw:min-w-full tw:text-sm">
                <thead className="tw:bg-gray-100">
                  <tr>
                    <th className="tw:text-left tw:px-3 tw:py-2">Data</th>
                    <th className="tw:text-left tw:px-3 tw:py-2">Utente</th>
                    <th className="tw:text-left tw:px-3 tw:py-2">Quiz</th>
                    <th className="tw:text-left tw:px-3 tw:py-2">Corrette/Totale</th>
                    <th className="tw:text-left tw:px-3 tw:py-2">Percentuale</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => {
                    const pct = r.percent !== undefined ? r.percent : (r.correct_count && r.total_questions ? (r.correct_count / r.total_questions) * 100 : 0);
                    return (
                      <tr
                        key={`res-${r.id || `${r.user_id}-${r.quiz_id}-${r.completed_at}`}`}
                        className="tw:border-b tw:border-gray-200 hover:tw:bg-gray-50 tw:cursor-pointer"
                        onClick={() => onSelect && onSelect(r)}
                      >
                        <td className="tw:px-3 tw:py-2">{r.completed_at || r.created_at || "—"}</td>
                        <td className="tw:px-3 tw:py-2">{`${r.firstname} ${r.lastname} • ${r.user_email || r.user_id}`}</td>
                        <td className="tw:px-3 tw:py-2">{r.quiz_id}</td>
                        <td className="tw:px-3 tw:py-2">{`${r.correct_count}/${r.total_questions}`}</td>
                        <td className={`tw:px-3 tw:py-2 ${pct >= 80 ? 'tw:text-green-600' : 'tw:text-red-600'}`}>{`${pct}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
